import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    MoreVertical,
    Pause,
    Play,
    Repeat,
    Shuffle,
    User,
    ListPlus,
    Heart,
    Pencil,
    Download,
    Ellipsis,
    CornerDownRight,
    Info,
    Trash2,
} from 'lucide-react';
import { AlbumCard } from '../components/PageComponents/cards/AlbumCard';
import { TrackMenu } from '../components/PageComponents/trackMenu/TrackMenu';
import { TrackInfoModal } from '../components/PageComponents/trackMenu/TrackInfoModal';
import { useTrackMenu } from '../hooks/useTrackMenu';
import { useDetailMenu } from '../hooks/useDetailMenu';
import { useFavourite } from '../hooks/useFavourite';
import { DetailMoreMenu } from '../components/PageComponents/detailMenu/DetailMoreMenu';
import { ArtistEditModal } from '../components/PageComponents/editModal/ArtistEditModal';
import { EntityInfoModal } from '../components/PageComponents/editModal/EntityInfoModal';
import { Album } from '../components/PageComponents/albumList/album';
import { getAlbumList, getAlbumsByArtistID, getTracksForAlbum, isTrackInAlbum } from '../components/PageComponents/albumList/getAllAlbums';
import { getTracksForArtistID } from '../components/PageComponents/trackList/getAll';
import { Artist } from '../components/PageComponents/artistList/artist';
import { getArtistByID } from '../components/PageComponents/artistList/getAllArtists';
import { Track } from '../components/PageComponents/trackList/track';
import { PlayerContext } from '../context/PlayerContext';
import { artistPhotoUrl, artistBackdropUrl } from '../config/images';
import { ArtistGenres } from '../components/PageComponents/artistList/ArtistGenres';
import { API_BASE } from '../config/api';
import './Detail.css';

export default function ArtistDetail(): React.JSX.Element {
    const { id } = useParams();
    const navigate = useNavigate();
    const {
        currentSong,
        isPlaying,
        tracks,
        refreshTracks,
        setQueue,
        playTrackNow,
        togglePlay,
        toggleShuffle,
        repeatMode,
        setRepeatMode,
        addTracksToQueue,
        playTracksNext,
    } = useContext(PlayerContext);
    const { menu, openAtCursor, openAtElement, closeMenu, infoTrack, openTrackInfo, closeTrackInfo } = useTrackMenu();
    const { position: moreMenuPosition, openAtElement: openMoreMenu, close: closeMoreMenu } = useDetailMenu();
    const [artist, setArtist] = useState<Artist | null>(null);
    const [allAlbums, setAllAlbums] = useState<Album[]>([]);
    const [artistAlbumsByID, setArtistAlbumsByID] = useState<Album[]>([]);
    const [artistTracksByID, setArtistTracksByID] = useState<Track[]>([]);
    const [loading, setLoading] = useState(true);
    const [photoFailed, setPhotoFailed] = useState(false);
    const [backdropFailed, setBackdropFailed] = useState(false);
    // Cache-buster for the hero photo/backdrop: the image URL is otherwise
    // identical before and after the edit modal changes the underlying
    // file, so the browser would keep showing its cached copy - bumping
    // this forces a re-fetch. See EditModal's own `bump` for the same
    // problem inside the modal's own previews.
    const [artVersion, setArtVersion] = useState(0);
    const [editing, setEditing] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    // Discography sort: newest release first by default, switchable to
    // oldest-first or title A-Z. Local state, not URL-driven - this list is
    // always fully loaded client-side, so no server paging is involved.
    const [albumSort, setAlbumSort] = useState<'newest' | 'oldest' | 'title'>('newest');
    const { favourited, toggle: toggleFavourite } = useFavourite('artist', artist?.getId());

    // Parallax: the backdrop scrolls slower than the rest of the page, so it
    // reads as sitting further back. .detailHeroBackdrop is CSS-only taller
    // than its visible window (see Detail.css) specifically to give this
    // translate room to move without exposing a gap - capped at that same
    // margin (160px) here. rAF-throttled since onScroll can fire far more
    // often than the display can actually repaint.
    const scrollRef = useRef<HTMLDivElement>(null);
    const rafRef = useRef<number | null>(null);
    const [scrollTop, setScrollTop] = useState(0);
    const parallaxOffset = Math.min(scrollTop * 0.6, 160);

    const handleScroll = useCallback(() => {
        if (rafRef.current !== null) return;
        rafRef.current = requestAnimationFrame(() => {
            rafRef.current = null;
            setScrollTop(scrollRef.current?.scrollTop ?? 0);
        });
    }, []);

    useEffect(() => {
        return () => {
            if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
        };
    }, []);

    useEffect(() => {
        refreshTracks();
        (async () => {
            setLoading(true);
            const artistId = Number(id);
            const [a, list, albums, tracksForArtist] = await Promise.all([
                getArtistByID(artistId),
                getAlbumList(),
                getAlbumsByArtistID(artistId),
                getTracksForArtistID(artistId),
            ]);
            setArtist(a);
            setAllAlbums(list);
            setArtistAlbumsByID(albums);
            setArtistTracksByID(tracksForArtist);
            setPhotoFailed(false);
            setBackdropFailed(false);
            setArtVersion(0);
            setLoading(false);
        })();
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
        setScrollTop(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Credited tracks/albums via track_artist/album_artist (features and
    // collaborations included), fetched by ID. Falls back to matching this
    // artist's name against the global track list when the ID-based fetch
    // comes back empty (e.g. pre-merge rows with no track_artist links yet).
    const artistTracks = useMemo(() => {
        if (!artist) return [];
        if (artistTracksByID.length > 0) return artistTracksByID;
        return tracks.filter((t) => t.getArtist() === artist.getTitle());
    }, [artist, tracks, artistTracksByID]);

    const artistAlbums = useMemo(() => {
        if (artistAlbumsByID.length > 0) return artistAlbumsByID;
        const titles = new Set(artistTracks.map((t) => t.getAlbum()));
        return allAlbums.filter((a) => titles.has(a.getTitle()));
    }, [artistAlbumsByID, artistTracks, allAlbums]);

    // Sorted view of the discography. Missing release dates sort last
    // regardless of direction, so albums without a year never bury the
    // dated ones at a fixed end.
    const sortedAlbums = useMemo(() => {
        const copy = [...artistAlbums];
        if (albumSort === 'title') {
            copy.sort((a, b) => a.getTitle().localeCompare(b.getTitle(), undefined, { sensitivity: 'base' }));
        } else {
            const dir = albumSort === 'newest' ? -1 : 1;
            copy.sort((a, b) => {
                const da = a.getReleaseDate();
                const db = b.getReleaseDate();
                if (da === db) return 0;
                if (!da) return 1;
                if (!db) return -1;
                return da < db ? -dir : dir;
            });
        }
        return copy;
    }, [artistAlbums, albumSort]);

    const handlePlay = (track: Track) => {
        if (currentSong?.getId() === track.getId()) togglePlay();
        else playTrackNow(track);
    };

    const handlePlayAll = () => {
        setQueue(artistTracks, 0);
    };

    // See albumDetail.tsx's identical handlers for why these start a fresh
    // setQueue rather than toggling shuffle/repeat on whatever's currently
    // playing.
    const handleShuffleAll = () => {
        setQueue(artistTracks, 0);
        toggleShuffle();
    };

    const handleLoopAll = () => {
        setQueue(artistTracks, 0);
        setRepeatMode('all');
    };

    const handleDelete = async () => {
        if (!artist) return;
        if (
            !window.confirm(
                `Remove "${artist.getTitle()}" from your library? Files on disk aren't touched, and you can restore it later from Settings → Library.`
            )
        ) {
            return;
        }
        const response = await fetch(`${API_BASE}/api/artist?id=${artist.getId()}`, {
            method: 'DELETE',
            credentials: 'include',
        });
        if (response.ok) navigate('/artists');
    };

    if (loading) {
        return (
            <div className="pageContent">
                <div className="trackListEmpty">Loading artist…</div>
            </div>
        );
    }

    if (!artist) {
        return (
            <div className="pageContent">
                <div className="trackListEmpty">Artist not found</div>
            </div>
        );
    }

    const photo = artistPhotoUrl(artist.getId(), artVersion);
    const backdrop = artistBackdropUrl(artist.getId(), artVersion);

    return (
        <div className="pageContent" ref={scrollRef} onScroll={handleScroll}>
            <div className="detailHeroWrapper">
                {backdrop && !backdropFailed && (
                    <div
                        className="detailHeroBackdrop"
                        style={{
                            backgroundImage: `url(${backdrop})`,
                            transform: `translateY(${parallaxOffset}px)`,
                        }}
                    >
                        <img
                            src={backdrop}
                            alt=""
                            aria-hidden="true"
                            style={{ display: 'none' }}
                            onError={() => setBackdropFailed(true)}
                        />
                    </div>
                )}
                <div className="detailHero">
                    <div className="detailHeroArt detailHeroArtCircle">
                        {photo && !photoFailed ? (
                            <img src={photo} alt={artist.getTitle()} onError={() => setPhotoFailed(true)} />
                        ) : (
                            <User size={40} strokeWidth={2.75} />
                        )}
                    </div>
                    <div className="detailHeroMeta">
                        <span className="detailHeroKicker">Artist</span>
                        <h1 className="detailHeroTitle">{artist.getTitle()}</h1>
                        {artist.getDescription() && <span className="detailHeroSub">{artist.getDescription()}</span>}
                        {(artist.getOrigin() || artist.getBegin()) && (
                            <span className="detailHeroMuted">
                                {[artist.getOrigin(), artist.getBegin()?.slice(0, 4)].filter(Boolean).join(' · ')}
                            </span>
                        )}
                        <ArtistGenres artistId={artist.getId()} />
                        <div className="detailHeroActions">
                            {artistTracks.length > 0 && (
                                <>
                                    <button className="detailPlayButton" onClick={handlePlayAll}>
                                        <Play size={16} strokeWidth={2.75} fill="currentColor" />
                                        Play
                                    </button>
                                    <button className="detailPillButton" onClick={handleShuffleAll}>
                                        <Shuffle size={15} strokeWidth={2.75} />
                                        Shuffle
                                    </button>
                                    <button
                                        className={`detailPillButton ${repeatMode !== 'off' ? 'detailPillButtonActive' : ''}`}
                                        onClick={handleLoopAll}
                                    >
                                        <Repeat size={15} strokeWidth={2.75} />
                                        Repeat
                                    </button>
                                    <button className="detailPillButton" onClick={() => addTracksToQueue(artistTracks)}>
                                        <ListPlus size={15} strokeWidth={2.75} />
                                        Queue
                                    </button>
                                </>
                            )}
                            <button
                                className={`detailIconButton ${favourited ? 'detailIconButtonActive' : ''}`}
                                onClick={toggleFavourite}
                                aria-label={favourited ? 'Remove from favourites' : 'Add to favourites'}
                            >
                                <Heart size={16} strokeWidth={2.75} fill={favourited ? 'currentColor' : 'none'} />
                            </button>
                            <button className="detailIconButton" onClick={() => setEditing(true)} aria-label="Edit artist">
                                <Pencil size={16} strokeWidth={2.75} />
                            </button>
                            <a
                                className="detailIconButton"
                                href={`${API_BASE}/api/download?type=artist&id=${artist.getId()}`}
                                download
                                aria-label="Download all tracks"
                            >
                                <Download size={16} strokeWidth={2.75} />
                            </a>
                            <button className="detailIconButton" onClick={openMoreMenu} aria-label="More options">
                                <Ellipsis size={16} strokeWidth={2.75} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {artistAlbums.length > 0 && (
                <div className="detailRelated">
                    <div className="detailRelatedHeader">
                        <h2 className="detailRelatedTitle">Albums</h2>
                        <select
                            className="detailSortSelect"
                            value={albumSort}
                            onChange={(e) => setAlbumSort(e.target.value as 'newest' | 'oldest' | 'title')}
                            aria-label="Sort albums"
                        >
                            <option value="newest">Newest first</option>
                            <option value="oldest">Oldest first</option>
                            <option value="title">Title A-Z</option>
                        </select>
                    </div>
                    <div className="cardGrid">
                        {sortedAlbums.map((a) => {
                            const isThisAlbumCurrent = isTrackInAlbum(currentSong, a);
                            return (
                                <AlbumCard
                                    key={a.getId()}
                                    album={a}
                                    artistName={artist.getTitle()}
                                    onClick={(clicked) => navigate(`/albums/${clicked.getId()}`)}
                                    isPlaying={isThisAlbumCurrent && isPlaying}
                                    onPlay={(clicked) => {
                                        if (isThisAlbumCurrent) togglePlay();
                                        else setQueue(getTracksForAlbum(tracks, clicked), 0);
                                    }}
                                />
                            );
                        })}
                    </div>
                </div>
            )}

            {artistTracks.length > 0 && (
                <>
                    <div className="detailRelated">
                        <div className="detailRelatedHeader">
                            <h2 className="detailRelatedTitle">Tracks</h2>
                        </div>
                    </div>
                    <div className="detailTrackList">
                        {artistTracks.map((track) => {
                            const isThisTrackPlaying = currentSong?.getId() === track.getId() && isPlaying;
                            return (
                                <div
                                    key={track.getId()}
                                    className={`detailTrackRow ${
                                        currentSong?.getId() === track.getId() ? 'detailTrackRowActive' : ''
                                    }`}
                                    onClick={() => handlePlay(track)}
                                    onContextMenu={(e) => openAtCursor(e, track)}
                                >
                                    <span className="detailTrackNumber">
                                        {isThisTrackPlaying ? (
                                            <Pause
                                                className="detailTrackNumberPlayIcon"
                                                size={14}
                                                strokeWidth={2.75}
                                                fill="currentColor"
                                            />
                                        ) : (
                                            <Play
                                                className="detailTrackNumberPlayIcon"
                                                size={14}
                                                strokeWidth={2.75}
                                                fill="currentColor"
                                            />
                                        )}
                                    </span>
                                    <span className="detailTrackTitle">{track.getTitle()}</span>
                                    <span className="detailTrackDuration">{track.getAlbum()}</span>
                                    <button
                                        className="detailTrackMenuButton"
                                        onClick={(e) => openAtElement(e, track)}
                                        aria-label={`More options for ${track.getTitle()}`}
                                    >
                                        <MoreVertical size={16} strokeWidth={2.75} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            {menu && (
                <TrackMenu track={menu.track} x={menu.x} y={menu.y} onClose={closeMenu} onShowInfo={openTrackInfo} />
            )}
            {infoTrack && <TrackInfoModal track={infoTrack} onClose={closeTrackInfo} />}

            {editing && (
                <ArtistEditModal
                    artist={artist}
                    onClose={() => setEditing(false)}
                    onSaved={(updated) => {
                        setArtist(updated);
                        setPhotoFailed(false);
                        setBackdropFailed(false);
                        setArtVersion((v) => v + 1);
                        refreshTracks();
                    }}
                />
            )}
            {showInfo && (
                <EntityInfoModal
                    title="Artist info"
                    rows={[
                        ['Name', artist.getTitle()],
                        ['Origin', artist.getOrigin()],
                        ['Formed / born', artist.getBegin()],
                        ['Albums', String(artistAlbums.length)],
                        ['Tracks', String(artistTracks.length)],
                        ['MusicBrainz ID', artist.getMbid()],
                    ]}
                    onClose={() => setShowInfo(false)}
                />
            )}
            {moreMenuPosition && (
                <DetailMoreMenu
                    x={moreMenuPosition.x}
                    y={moreMenuPosition.y}
                    onClose={closeMoreMenu}
                    tracks={artistTracks}
                    itemsBefore={[
                        { label: 'Play now', icon: Play, onClick: handlePlayAll },
                        { label: 'Play next', icon: CornerDownRight, onClick: () => playTracksNext(artistTracks) },
                        { label: 'Add to queue', icon: ListPlus, onClick: () => addTracksToQueue(artistTracks) },
                    ]}
                    itemsAfter={[
                        {
                            label: 'Download',
                            icon: Download,
                            onClick: () => window.location.assign(`${API_BASE}/api/download?type=artist&id=${artist.getId()}`),
                        },
                        { label: 'Artist info', icon: Info, onClick: () => setShowInfo(true) },
                        { label: 'Remove from library', icon: Trash2, onClick: handleDelete, danger: true },
                    ]}
                />
            )}
        </div>
    );
}
